import { useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Archive as ArchiveIcon, Keyboard, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import {
    computeMonthlyProgress,
    todayStr,
    currentMonthKey,
    monthKey,
    formatDatePretty,
} from "../lib/progress";
import { ROLES, isActive } from "../lib/auth";
import { StudentProfileCard } from "../components/student-detail/StudentProfileCard";
import { PendingMakeupBanner } from "../components/student-detail/PendingMakeupBanner";
import { QuickAttendancePanel } from "../components/student-detail/QuickAttendancePanel";
import { AttendanceHistoryRow } from "../components/student-detail/AttendanceHistoryRow";
import { StudentDetailHeader } from "../components/student-detail/StudentDetailHeader";
import { StudentNotesCard } from "../components/student-detail/StudentNotesCard";

export default function StudentDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const {
        getAnyStudent,
        students,
        attendance,
        saveAttendance,
        removeAttendance,
        resolveMakeup,
        archiveStudent,
        restoreStudent,
        transferStudent,
        updateStudent,
        scope,
    } = useData();
    const { isAdmin, teachers } = useAuth();
    const { settings } = useSettings();

    const student = getAnyStudent(id);
    const [flashId, setFlashId] = useState(null);
    const panelRef = useRef(null);

    const records = useMemo(
        () =>
            attendance
                .filter((r) => r.studentId === id)
                .sort((a, b) => b.date.localeCompare(a.date)),
        [attendance, id],
    );

    const pendingMakeups = useMemo(
        () =>
            records.filter(
                (r) =>
                    r.status === "Excused" && !(r.remark || "").startsWith("Done"),
            ),
        [records],
    );

    const monthStats = useMemo(() => {
        const mk = currentMonthKey();
        const monthRecords = records.filter((r) => monthKey(r.date) === mk);
        return {
            present: monthRecords.filter((r) => r.status === "Present").length,
            doubles: monthRecords.filter((r) => r.status === "Double Session").length,
            absent: monthRecords.filter((r) => r.status === "Absent").length,
            excused: monthRecords.filter((r) => r.status === "Excused").length,
        };
    }, [records]);

    const todayLogged = useMemo(
        () => records.find((r) => r.date === todayStr()),
        [records],
    );

    // For admin Transfer: pick from active teachers OTHER than the current owner.
    const teacherOptions = useMemo(
        () =>
            teachers.filter(
                (t) =>
                    t.role === ROLES.TEACHER &&
                    isActive(t) &&
                    t.id !== student?.teacherId,
            ),
        [teachers, student],
    );

    // Prev / next for arrow-key navigation. Computed from the active list
    // so archived students don't appear in the cycle.
    const studentIndex = students.findIndex((s) => s.id === id);
    const prevStudent = studentIndex > 0 ? students[studentIndex - 1] : null;
    const nextStudent =
        studentIndex !== -1 && studentIndex < students.length - 1
            ? students[studentIndex + 1]
            : null;

    const isArchived = !!student?.archivedAt;
    const shortcutsEnabled = !!student && !isArchived;

    useKeyboardShortcuts(
        [
            {
                keys: "ArrowLeft",
                when: () => !!prevStudent,
                handler: () => navigate(`/students/${prevStudent.id}`),
            },
            {
                keys: "ArrowRight",
                when: () => !!nextStudent,
                handler: () => navigate(`/students/${nextStudent.id}`),
            },
            {
                keys: ["t", "T"],
                handler: () => panelRef.current?.setToday(),
            },
            {
                keys: ["p", "P"],
                handler: () => panelRef.current?.setStatus("Present"),
            },
            {
                keys: ["a", "A"],
                handler: () => panelRef.current?.setStatus("Absent"),
            },
            {
                keys: ["e", "E"],
                handler: () => panelRef.current?.setStatus("Excused"),
            },
            {
                keys: ["d", "D"],
                handler: () => panelRef.current?.setStatus("Double Session"),
            },
            {
                keys: "Enter",
                meta: true,
                handler: () => panelRef.current?.save(),
            },
        ],
        { enabled: shortcutsEnabled },
    );

    if (!student) {
        return (
            <div className="text-center py-20" data-testid="student-not-found">
                <div className="font-display font-bold text-3xl uppercase mb-3">
                    Student not found
                </div>
                <Link to="/students" className="text-[#3D6385] font-bold">
                    Back to students
                </Link>
            </div>
        );
    }

    const progress = computeMonthlyProgress(
        student,
        attendance,
        undefined,
        settings.monthlyTargetMultiplier,
    );

    // Find ALL still-pending make-ups (Excused with no "Done" remark) for
    // this student, oldest first, optionally excluding one specific record.
    const findPendingMakeups = (excludeId, beforeDate) => {
        return [...attendance]
            .filter(
                (r) =>
                    r.studentId === student.id &&
                    r.status === "Excused" &&
                    !(r.remark || "").startsWith("Done") &&
                    (!excludeId || r.id !== excludeId) &&
                    (!beforeDate || r.date <= beforeDate),
            )
            .sort((a, b) => a.date.localeCompare(b.date));
    };

    // When a Double Session gets logged, one of the two lessons is typically
    // the make-up. Show a prominent card that lists ALL pending make-ups —
    // teacher taps the one this Double Session covered.
    const offerResolveMakeup = (savedRecord) => {
        if (!savedRecord || savedRecord.status !== "Double Session") return;
        const pendingList = findPendingMakeups(savedRecord.id, savedRecord.date);
        if (pendingList.length === 0) return;

        toast.custom(
            (t) => (
                <div
                    className="w-[420px] max-w-[92vw] rounded-2xl bg-white border-2 border-warning shadow-lift overflow-hidden fade-up"
                    data-testid="double-session-resolve-prompt"
                >
                    <div className="p-5">
                        <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-warning-soft text-warning flex items-center justify-center shrink-0">
                                <CalendarClock size={20} strokeWidth={2.4} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold text-base text-ink leading-tight">
                                    Resolve pending make-up?
                                </div>
                                <p className="mt-1.5 text-sm text-ink-soft leading-relaxed">
                                    A Double Session usually covers 1 regular lesson
                                    + 1 make-up. Pick which one this covered for{" "}
                                    <span className="font-semibold text-ink">
                                        {student.name.split(/\s+/)[0]}
                                    </span>
                                    .
                                </p>
                            </div>
                        </div>

                        <div
                            className={`relative ${pendingList.length > 3 ? "after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:right-0 after:h-6 after:bg-gradient-to-t after:from-white after:to-transparent" : ""}`}
                        >
                            <ul
                                className={`space-y-1.5 ${pendingList.length > 3 ? "max-h-[220px] overflow-y-auto pr-1 pb-1" : ""}`}
                                data-testid="double-session-makeup-list"
                            >
                            {pendingList.map((p) => (
                                <li key={p.id}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            resolveMakeup?.(p.id);
                                            toast.dismiss(t);
                                            toast.success(
                                                `Make-up from ${formatDatePretty(p.date)} resolved`,
                                            );
                                        }}
                                        className="focus-ring w-full flex items-center justify-between gap-3 rounded-lg bg-warning-soft/50 hover:bg-warning-soft border border-warning/30 hover:border-warning px-3 py-2.5 text-left transition-colors"
                                        data-testid={`double-session-resolve-${p.id}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="font-semibold text-sm text-ink truncate">
                                                {formatDatePretty(p.date)}
                                            </div>
                                            {p.remark && (
                                                <div className="text-[11px] text-ink-mute truncate">
                                                    {p.remark}
                                                </div>
                                            )}
                                        </div>
                                        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-warning bg-white border border-warning/40 rounded-full px-2 py-1">
                                            Resolve
                                        </span>
                                    </button>
                                </li>
                            ))}
                            </ul>
                        </div>

                        <div className="mt-4 flex items-center justify-end">
                            <button
                                type="button"
                                onClick={() => toast.dismiss(t)}
                                className="focus-ring h-9 px-4 rounded-lg text-xs font-semibold uppercase tracking-wider text-ink-mute hover:text-ink hover:bg-mist/60 transition-colors"
                                data-testid="double-session-dismiss-btn"
                            >
                                None of these
                            </button>
                        </div>
                    </div>
                </div>
            ),
            { duration: 15000, position: "top-center" },
        );
    };

    const handleSaved = (saved) => {
        setFlashId(saved.id);
        setTimeout(() => setFlashId(null), 1800);
        offerResolveMakeup(saved);
    };

    // Edit an existing attendance record inline (from Attendance History row).
    // Reuses saveAttendance which upserts on {studentId, date}.
    const handleEditRecord = (record, { status, remark }) => {
        if (isArchived) {
            toast.error("Restore student before editing history.");
            return;
        }
        const saved = saveAttendance({
            studentId: record.studentId,
            date: record.date,
            status,
            remark,
        });
        if (saved) {
            setFlashId(saved.id);
            setTimeout(() => setFlashId(null), 1800);
            toast.success("Record updated");
            offerResolveMakeup(saved);
        }
    };

    // Delete an existing attendance record.
    const handleDeleteRecord = (record) => {
        if (isArchived) {
            toast.error("Restore student before deleting history.");
            return;
        }
        const removed = removeAttendance({
            studentId: record.studentId,
            date: record.date,
        });
        if (removed) {
            toast.success("Record deleted", {
                action: {
                    label: "Undo",
                    onClick: () => {
                        const restored = saveAttendance({
                            studentId: removed.studentId,
                            date: removed.date,
                            status: removed.status,
                            remark: removed.remark,
                        });
                        if (restored) {
                            setFlashId(restored.id);
                            setTimeout(() => setFlashId(null), 1800);
                        }
                    },
                },
            });
        }
    };

    // Notes auto-save target. Disabled when archived (UX choice) or when admin
    // is in "All Studio" workspace (updateStudent would refuse with a toast).
    const notesDisabled =
        isArchived || (student.teacherId && scope !== student.teacherId);

    const saveNotes = (notes) => {
        if (notesDisabled) return;
        updateStudent(student.id, { notes });
    };

    return (
        <div className="space-y-6" data-testid="student-detail-page">
            <StudentDetailHeader
                student={student}
                students={students}
                isAdmin={isAdmin}
                teacherOptions={teacherOptions}
                archiveStudent={archiveStudent}
                restoreStudent={restoreStudent}
                transferStudent={transferStudent}
            />

            {isArchived && (
                <div
                    className="bg-warning-soft border border-warning/30 rounded-3xl px-5 sm:px-6 py-4 flex items-start sm:items-center gap-3"
                    data-testid="archived-banner"
                >
                    <div className="w-9 h-9 rounded-xl bg-warning-soft text-warning flex items-center justify-center shrink-0">
                        <ArchiveIcon size={18} strokeWidth={2.6} />
                    </div>
                    <div className="flex-1">
                        <div className="font-bold text-sm text-warning">
                            Archived student
                        </div>
                        <div className="text-xs text-warning/80">
                            Hidden from active lists. History below is preserved. Use
                            Restore (top-right) to bring them back.
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <StudentProfileCard student={student} progress={progress} />

                <section className="lg:col-span-2 space-y-6">
                    {!isArchived && (
                        <>
                            <PendingMakeupBanner pendingMakeups={pendingMakeups} />
                            <QuickAttendancePanel
                                ref={panelRef}
                                student={student}
                                saveAttendance={saveAttendance}
                                removeAttendance={removeAttendance}
                                todayLogged={todayLogged}
                                monthStats={monthStats}
                                onSaved={handleSaved}
                            />
                        </>
                    )}

                    <StudentNotesCard
                        student={student}
                        onSave={saveNotes}
                        disabled={notesDisabled}
                    />

                    <div className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="font-display font-bold text-xl uppercase tracking-tight">
                                Attendance History
                            </h2>
                            <span className="text-xs font-bold text-slate-500">
                                {records.length} record{records.length === 1 ? "" : "s"}
                            </span>
                        </div>

                        {records.length === 0 ? (
                            <div className="text-sm text-slate-500">
                                No attendance logged yet. Your first entry will appear here.
                            </div>
                        ) : (
                            <ul
                                className="divide-y divide-mist"
                                data-testid="attendance-history"
                            >
                                {records.map((r) => (
                                    <AttendanceHistoryRow
                                        key={r.id}
                                        record={r}
                                        isFlash={flashId === r.id}
                                        onEdit={handleEditRecord}
                                        onDelete={handleDeleteRecord}
                                        readOnly={isArchived}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                </section>
            </div>

            {shortcutsEnabled && (
                <div
                    className="hidden md:flex items-center gap-2 text-xs text-slate-500 justify-end"
                    data-testid="keyboard-shortcuts-hint"
                >
                    <Keyboard size={12} strokeWidth={2.8} className="text-slate-400" />
                    <span className="font-bold uppercase tracking-wider text-slate-400">
                        Shortcuts
                    </span>
                    <Kbd>←</Kbd>
                    <Kbd>→</Kbd>
                    <span className="text-slate-400">prev / next</span>
                    <span className="text-slate-200">·</span>
                    <Kbd>T</Kbd>
                    <span className="text-slate-400">today</span>
                    <span className="text-slate-200">·</span>
                    <Kbd>P</Kbd>
                    <Kbd>A</Kbd>
                    <Kbd>E</Kbd>
                    <Kbd>D</Kbd>
                    <span className="text-slate-400">status</span>
                    <span className="text-slate-200">·</span>
                    <Kbd>⌘</Kbd>
                    <Kbd>↵</Kbd>
                    <span className="text-slate-400">save</span>
                </div>
            )}
        </div>
    );
}

function Kbd({ children }) {
    return (
        <kbd className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded border border-mist bg-mist/50 text-[10px] font-bold text-slate-600 font-mono">
            {children}
        </kbd>
    );
}
